/* @HEADER
 * @version 1.1.950 | 2026-10-26
 * @purpose Answers 'does the server let this person see this space now' with the server's explicit may-open: which ids it asks under and what each answer becomes (ZVX-DEV-8344, ZVX-4685; R9 case 2 of the navigational links).
 * @sidecar navLinkSpaceRight.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8344, ZVX-DEV-8263, ZVX-DEV-7134

/**
 * «Does the server let this person see this space now» (R9 of docs/prd/navigational-links.md), answered by the server itself:
 * `GET /api/content-access/may-open` (ZVX-DEV-8343) says `{ mayOpen }`, from the one function of right the blob route shares
 * (membership or an invitation, the transfer authorizer, a live row of the catalog). It used to be asked of the relay's body
 * route, which a SPACE does not have: a space is a row of the freshness catalog, so for a space the person may see the relay
 * answered 404 and this module read every 404 as a refusal. The owner's link stayed «Ссылка недоступна» (measured 2026-10-26).
 *
 * Why the SPACE and never the board or the card: the server closes a board or a card of a space only when their rows name
 * the space (`space_id`). Asking about the parent is the question that is closed everywhere; a board's own answer is not.
 *
 * Which id to ask under. The link carries the server's id of the space (the uuid of the spaces registry). The server finds the
 * row stored under the id its creator gave it by the server id the row carries, so the id of the link is asked first. The ids
 * this device holds the space under are asked after it (a row that has no server id yet is found only by its own id). The
 * built-in space is asked under its project-scoped id EXACTLY (`default::<project>`), never under the bare literal, which names
 * no one. Any `true` opens; all `false` refuse; a question that could not be asked says the answer did not come.
 *
 * Known limit (ZVX-4706): a guest who is not a member and was invited to a space is admitted by the id as asked, so asking by the
 * uuid of a link refuses them.
 */

import type { SpaceRight } from './navLinkResolve.ts';
// @ts-expect-error -- framework-free .mjs, typed by its JSDoc
import { DEFAULT_SPACE_LOCAL_ID, buildSpaceWireContentId } from '../features/content-p2p/spaceCatalogSync.mjs';

export interface MayOpenAsk {
  orgId: string;
  contentType: 'space';
  contentId: string;
}

export interface SpaceRightDeps {
  /** The server's `mayOpen` for this item; REJECTS when the question could not be asked or the answer could not be read. */
  mayOpen: (ask: MayOpenAsk) => Promise<boolean>;
  /** The ids under which THIS device holds the space with that server id (e.g. its `bg-<ms>`); may be empty. */
  localKeysFor(spaceServerId: string): readonly string[];
}

/** The ids to ask, most likely first, without repeats. */
export function spaceIdsToAsk(spaceId: string, projectServerId: string | null, localKeys: readonly string[]): string[] {
  const ids: string[] = [];
  const add = (id: unknown) => {
    if (typeof id === 'string' && id.length > 0 && !ids.includes(id)) ids.push(id);
  };
  if (spaceId === DEFAULT_SPACE_LOCAL_ID) {
    // The built-in space is the same literal in every project: its id is scoped by the project, and unscoped it names no one.
    if (projectServerId === null) return [];
    add(buildSpaceWireContentId(DEFAULT_SPACE_LOCAL_ID, projectServerId));
    return ids;
  }
  add(spaceId);
  for (const id of localKeys) add(id);
  return ids;
}

export function createSpaceRight(deps: SpaceRightDeps) {
  return async function spaceRight(ask: {
    organisationId: string;
    projectServerId: string | null;
    spaceId: string;
  }): Promise<SpaceRight> {
    const ids = spaceIdsToAsk(ask.spaceId, ask.projectServerId, deps.localKeysFor(ask.spaceId));
    if (ids.length === 0) return 'refused';
    let unreachable = false;
    for (const contentId of ids) {
      try {
        if (await deps.mayOpen({ orgId: ask.organisationId, contentType: 'space', contentId })) return 'granted';
      } catch {
        // An id that could not be asked is not a "no": say the answer did not come, unless another id says yes.
        unreachable = true;
      }
    }
    return unreachable ? 'unreachable' : 'refused';
  };
}
